import PropTypes from 'prop-types';
import React from 'react';
import rgba from 'polished/lib/color/rgba';
import styled from 'styled-components';

import Fade from 'ravenjs/lib/Fade';
import { getThemeProps } from 'ravenjs/utils/theme';

const BackdropStyled = styled.div`
    align-items: ${({ alignItems }) => alignItems || 'center'};
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    position: ${({ position }) => position || 'fixed'};
    right: 0;
    top: 0;
    user-select: none;
    z-index: ${({ zIndex }) => zIndex || 1000};
    backdrop-filter: ${({ blur }) => blur && `blur(${blur})`};
    -webkit-backdrop-filter: ${({ blur }) => blur && `blur(${blur})`};
    /**
     * Add all of the remaining styles from theme
     */
    ${getThemeProps('Backdrop.styles')};
    /**
     * Add dynamic styles.
     */
    ${({ theme, color, opacity }) => {
        const backgroundColor = rgba(
            getThemeProps(`palette.${color}.color`, '#2D2D2D', {
                theme,
            }),
            opacity
        );

        return {
            backgroundColor,
        };
    }};
`;

/**
 * Increment the transition duration for inner transition to take effect.
 *
 * @method incrementInnerDuration
 * @param  {number|Object}        duration The transition duration
 * @return {number|Object}
 */
const incrementInnerDuration = duration => {
    const INNER_DURATION = 350;

    if (typeof duration === 'number') {
        return duration + INNER_DURATION;
    }

    if (duration && typeof duration === 'object') {
        return {
            enter: duration.enter + INNER_DURATION,
            exit: duration.exit + INNER_DURATION,
        };
    }

    return INNER_DURATION;
};

const Backdrop = ({
    TransitionComponent,
    children,
    onBackdropClick,
    opacity,
    open,
    position,
    transitionDuration,
    color,
    zIndex,
    blur,
    ...other
}) => (
    <Fade appear in={open} timeout={transitionDuration} {...other}>
        <BackdropStyled
            aria-hidden="true"
            onClick={onBackdropClick}
            opacity={opacity}
            position={position}
            color={color}
            zIndex={zIndex}
            blur={blur}
        >
            <TransitionComponent appear in timeout={incrementInnerDuration(transitionDuration)}>
                {children}
            </TransitionComponent>
        </BackdropStyled>
    </Fade>
);

Backdrop.propTypes = {
    alignItems: PropTypes.string,
    /**
     * A Transition Component.
     */
    TransitionComponent: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
    /**
     * The main content for the backdrop.
     */
    children: PropTypes.node,
    /**
     * Apply themed styling to Backdrop.
     *
     * Colors can be defined in `theme.palette`.
     */
    color: PropTypes.string,
    /**
     * Callback fired on backdrop click event.
     */
    onBackdropClick: PropTypes.func,
    /**
     * The opacity of the background color.
     */
    opacity: PropTypes.number,
    /**
     * If `true`, the Backdrop is open.
     */
    open: PropTypes.bool,
    /**
     * Sets the 'position' css property.
     */
    position: PropTypes.oneOf(['absolute', 'fixed', 'relative']),
    /**
     * The duration for the transition, in milliseconds.
     */
    transitionDuration: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.shape({ enter: PropTypes.number, exit: PropTypes.number }),
    ]),
    zIndex: PropTypes.number,
    /**
     * Sets the 'blur' css property.
     */
    blur: PropTypes.string,
};

Backdrop.defaultProps = {
    alignItems: 'center',
    TransitionComponent: Fade,
    children: null,
    color: 'dark',
    onBackdropClick: () => {},
    opacity: 0.5,
    open: false,
    position: 'fixed',
    transitionDuration: 0,
    zIndex: 1000,
    blur: '0px',
};

export default Backdrop;
